<!-- FT-216 REQ-03: диалог новой ручной поправки на даты — диапазон дат, процент, область
     («весь филиал» / ценовые группы / выбранные машины). Сохраняется своим запросом. До сохранения
     предпросмотр (тот же POST с dry_run) предупреждает о выходе цены за мин./макс. машины — сохранить
     всё равно можно (явное решение админа). -->
<template>
  <v-dialog :value="value" max-width="460" scrollable @input="onDialogInput">
    <v-card data-testid="dp-dialog">
      <v-card-title class="text-h6">{{ $t("dynamic_pricing.dialog_title") }}</v-card-title>
      <v-card-text>
        <div class="text-caption text--secondary mb-1">{{ $t("dynamic_pricing.dates_label") }}</div>
        <v-date-picker v-model="range" range no-title full-width :min="today" :locale="$i18n && $i18n.locale"
          first-day-of-week="1" class="elevation-0 dp-picker" data-testid="dp-range" />
        <div class="text-body-2 mb-3" data-testid="dp-range-text">
          {{ rangeText || $t("dynamic_pricing.dates_hint") }}
        </div>

        <v-text-field v-model="percentText" type="number" step="1" :min="PERCENT_MIN" :max="PERCENT_MAX" suffix="%"
          :label="$t('dynamic_pricing.percent_label')" :hint="$t('dynamic_pricing.percent_hint')" persistent-hint
          :rules="percentRules" dense outlined class="mb-3" data-testid="dp-percent" />

        <div class="text-caption text--secondary">{{ $t("dynamic_pricing.scope_label") }}</div>
        <v-radio-group v-model="scope" dense hide-details class="mt-1 mb-2" data-testid="dp-scope">
          <v-radio value="company" :label="$t('dynamic_pricing.scope_company')" data-testid="dp-scope-company" />
          <v-radio value="groups" :label="$t('dynamic_pricing.scope_groups')" data-testid="dp-scope-groups" />
          <v-radio value="cars" :label="$t('dynamic_pricing.scope_cars')" data-testid="dp-scope-cars" />
        </v-radio-group>

        <v-select v-if="scope === 'groups'" v-model="groupIds" :items="groupItems" multiple chips small-chips
          deletable-chips dense outlined hide-details :loading="optionsLoading" :label="$t('dynamic_pricing.groups_field')"
          data-testid="dp-groups" />
        <v-autocomplete v-if="scope === 'cars'" v-model="carIds" :items="carItems" item-text="text" item-value="id"
          multiple chips small-chips deletable-chips dense outlined hide-details hide-no-data
          :loading="optionsLoading" :label="$t('dynamic_pricing.cars_field')" data-testid="dp-cars" />

        <v-alert v-if="boundsWarning" type="warning" dense text class="mt-3 mb-0" data-testid="dp-preview-warning">
          {{ $t("dynamic_pricing.preview_out_of_car_bounds") }}
        </v-alert>
        <v-alert v-if="errorText" type="error" dense text class="mt-3 mb-0" data-testid="dp-error">{{ errorText }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" data-testid="dp-cancel" @click="close">{{ $t("custom.cancel") }}</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!canSave" data-testid="dp-save" @click="save">
          {{ $t("custom.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { dynamicPricingApi, parseDynamicPricingError } from "@/api/dynamicPricing";
import { fetchPriceGroups } from "@/utils/priceGroupsApi";
import { priceGroupLabel, priceGroupLabelOptions, sortByMinPrice } from "@/utils/priceGroupLabel";
import { companyNow } from "@/utils/wallClock";

export const PERCENT_MIN = -50;
export const PERCENT_MAX = 100;
// Пауза перед предпросмотром dry_run после последней правки ввода.
export const PREVIEW_DEBOUNCE_MS = 350;

// Сегодня по часам филиала, YYYY-MM-DD — нижняя граница календаря (прошедшие дни закрыты). Сервер сверяет
// starts_on с той же датой (Company#wall_clock_now, пояс companies.time_zone, пустой — UTC) и отвечает 422
// starts_in_past: по дате браузера администратор из другого пояса не смог бы выбрать ещё идущий у филиала день
// или выбрал бы уже прошедший.
export function branchTodayIso(timeZone, now = new Date()) {
  return companyNow(timeZone || "UTC", now).format("YYYY-MM-DD");
}

// 2026-10-31 → "31.10.2026"
function formatIso(iso) {
  const m = typeof iso === "string" && iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

// Период поправки: один день — одна дата, иначе «31.10.2026 – 02.11.2026». Общий с DynamicPricingPanel.
export function formatAdjustmentPeriod(startsOn, endsOn) {
  return startsOn === endsOn ? formatIso(startsOn) : `${formatIso(startsOn)} – ${formatIso(endsOn)}`;
}

// Процент из поля ввода: пусто или не число — null (сохранить нельзя). Диапазон −50…+100 клиент проверяет для
// блокировки кнопки, окончательно решает api (422 percent_out_of_range).
export function parsePercent(text) {
  if (text === "" || text == null) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export default {
  name: "DateAdjustmentDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      PERCENT_MIN,
      PERCENT_MAX,
      range: [],
      percentText: "",
      scope: "company",
      groupIds: [],
      carIds: [],
      groups: [],
      groupsUnit: null,
      cars: [],
      groupsLoaded: false,
      carsLoaded: false,
      optionsLoading: false,
      saving: false,
      errorText: "",
      today: branchTodayIso(this.branchTimeZone()),
      // Каталоги групп и машин — свои на каждое открытие (группы могли пересобрать в PriceGroupsBlock).
      // catalogSeq отсекает ответ загрузки из прошлого открытия.
      catalogSeq: 0,
      // Предпросмотр: warnings последнего актуального ответа dry_run; previewSeq отсекает устаревшие ответы.
      previewDelay: PREVIEW_DEBOUNCE_MS,
      previewWarnings: [],
      previewSeq: 0,
      previewTimer: null,
    };
  },
  computed: {
    sortedRange() {
      return (this.range || []).slice().sort();
    },
    // Одна дата — поправка на один день: ends_on = starts_on (включительно, REQ-03).
    startsOn() {
      return this.sortedRange[0] || null;
    },
    endsOn() {
      return this.sortedRange[1] || this.sortedRange[0] || null;
    },
    rangeText() {
      if (!this.startsOn) return "";
      return formatAdjustmentPeriod(this.startsOn, this.endsOn);
    },
    percent() {
      return parsePercent(this.percentText);
    },
    percentValid() {
      // 0 % цену не меняет, а округлил бы день (REQ-07) — api отвечает percent_out_of_range
      return this.percent != null && this.percent !== 0 && this.percent >= PERCENT_MIN && this.percent <= PERCENT_MAX;
    },
    scopeValid() {
      if (this.scope === "groups") return this.groupIds.length > 0;
      if (this.scope === "cars") return this.carIds.length > 0;
      return true;
    },
    // Ввод полный и валидный по клиентским правилам — можно сохранять и спрашивать предпросмотр.
    inputValid() {
      return !!this.startsOn && this.percentValid && this.scopeValid;
    },
    canSave() {
      return !this.saving && this.inputValid;
    },
    // Ключ предпросмотра: меняется вместе с телом запроса; null — ввод неполный, спрашивать нечего.
    previewKey() {
      return this.inputValid ? JSON.stringify(this.buildPayload()) : null;
    },
    boundsWarning() {
      return this.previewWarnings.includes("out_of_car_bounds");
    },
    // Поле процента объясняет, почему «Сохранить» закрыта; пустое поле не ругаем — это ещё не ввод.
    percentRules() {
      return [
        (v) => v === "" || v == null || this.percent !== 0 || this.$t("dynamic_pricing.errors.percent_zero"),
        (v) => v === "" || v == null || this.percent === 0 || this.percentValid ||
          this.$t("dynamic_pricing.errors.percent_out_of_range"),
      ];
    },
    groupItems() {
      const options = priceGroupLabelOptions(this, this.groupsUnit);
      return sortByMinPrice(this.groups).map((g) => ({ text: priceGroupLabel(g, options), value: g.id }));
    },
    carItems() {
      return this.cars.map((c) => ({ id: c.id, text: c.code || c.car_name || String(c.id) }));
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        this.invalidateCatalogs();
      } else {
        this.cancelPreview();
      }
    },
    previewKey() {
      this.schedulePreview();
    },
    scope(value) {
      if (value === "groups") this.loadGroups();
      if (value === "cars") this.loadCars();
    },
  },
  beforeDestroy() {
    this.cancelPreview();
  },
  methods: {
    // Пояс текущего филиала (companies.time_zone); нет — undefined (branchTodayIso считает по UTC, как сервер).
    branchTimeZone() {
      const company = this.$store && this.$store.getters && this.$store.getters.getCompany;
      return (company && company.company && company.company.time_zone) || undefined;
    },
    reset() {
      this.range = [];
      this.percentText = "";
      this.scope = "company";
      this.groupIds = [];
      this.carIds = [];
      this.errorText = "";
      this.today = branchTodayIso(this.branchTimeZone());
      this.previewWarnings = [];
    },
    // Следующий выбор области заново грузит каталог; ответ прошлой загрузки отбрасывается.
    invalidateCatalogs() {
      this.groupsLoaded = false;
      this.carsLoaded = false;
      this.catalogSeq += 1;
    },
    onDialogInput(open) {
      if (!open) this.close();
    },
    close() {
      if (this.saving) return;
      this.$emit("input", false);
    },
    async loadGroups() {
      if (this.groupsLoaded) return;
      const seq = this.catalogSeq;
      this.optionsLoading = true;
      try {
        const data = await fetchPriceGroups(this);
        if (seq !== this.catalogSeq) return;
        this.groups = data.groups || [];
        this.groupsUnit = data.unit || null;
        this.groupsLoaded = true;
        // архивная после пересборки группа из выбора уходит — иначе api ответит 422 unknown_price_group
        this.groupIds = this.groupIds.filter((id) => this.groups.some((g) => g.id === id));
      } catch (error) {
        this.setError && this.setError(error, this.$t("price_groups.load_error"));
      } finally {
        this.optionsLoading = false;
      }
    },
    // Машины филиала — тот же список, что в других формах (jsonapi: data[].attributes). Выбор «участвует = нет»
    // и чужие машины отсекает api (422 car_not_participating / unknown_car).
    async loadCars() {
      if (this.carsLoaded) return;
      const seq = this.catalogSeq;
      this.optionsLoading = true;
      try {
        const response = await this.axios.get("/api/v1/cars.json", {
          headers: { Authorization: this.$store.getters.getAuthToken },
        });
        if (seq !== this.catalogSeq) return;
        const rows = (response.data && response.data.data) || [];
        this.cars = rows.map((row) => row.attributes).filter((attrs) => attrs && attrs.id != null);
        this.carsLoaded = true;
        this.carIds = this.carIds.filter((id) => this.cars.some((c) => c.id === id));
      } catch (error) {
        this.setError && this.setError(error, this.$t("errors.cars_load"));
      } finally {
        this.optionsLoading = false;
      }
    },
    buildPayload() {
      const payload = { starts_on: this.startsOn, ends_on: this.endsOn, percent: this.percent, scope: this.scope };
      if (this.scope === "groups") payload.price_group_ids = this.groupIds.slice();
      if (this.scope === "cars") payload.car_ids = this.carIds.slice();
      return payload;
    },
    // Предпросмотр dry_run с дебаунсом: каждая правка ввода сбрасывает прежний таймер и плашку,
    // ответ учитывается, только если после него ввод не менялся.
    schedulePreview() {
      this.cancelPreview();
      this.previewWarnings = [];
      if (!this.value || !this.previewKey) return;
      const seq = this.previewSeq;
      this.previewTimer = setTimeout(() => this.runPreview(seq), this.previewDelay);
    },
    cancelPreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = null;
      this.previewSeq += 1;
    },
    async runPreview(seq) {
      this.previewTimer = null;
      try {
        const data = await dynamicPricingApi.previewAdjustment(this.axios, this.buildPayload());
        if (seq !== this.previewSeq) return;
        this.previewWarnings = data && Array.isArray(data.warnings) ? data.warnings : [];
      } catch (error) {
        // Предпросмотр — только подсказка: отказ (422, сеть) покажет сохранение, здесь без шума и Rollbar.
      }
    },
    async save() {
      if (!this.canSave) return;
      this.cancelPreview(); // плашка остаётся, новый запрос предпросмотра не нужен
      this.saving = true;
      this.errorText = "";
      try {
        const created = await dynamicPricingApi.createAdjustment(this.axios, this.buildPayload());
        this.$emit("created", created || {});
        this.$emit("input", false);
      } catch (error) {
        const parsed = parseDynamicPricingError(error);
        if (parsed) this.errorText = this.$t(parsed.i18nKey);
        else this.setError && this.setError(error, this.$t("dynamic_pricing.create_error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.dp-picker {
  max-width: 100%;
}
</style>
